import React, { Component } from 'react'
import { action, observable, makeObservable } from 'mobx'
import { observer } from 'mobx-react'
import classNames from 'classnames'

import { EloInfoMessage } from '@elo-ui/components/elo-info-message'
import { EloInfoIcon } from '@elo-ui/components/icons/regular'

import { getSearchParams } from 'utils/queryString.utils'
import { COURSE_CONTENT_CLASS_NAMES } from '@elo-kit/constants/customCss.constants'
import { formatDateTime } from '@elo-kit/utils/dateTime.utils'

import { withStores } from 'utils/helpers.utils'
import { getTimeLeftObject } from 'utils/dateTime.utils'

import { THEMES } from 'constants/themes.constants'

import ContentPage from 'shared/components/content-page/preview'
import CommunityPreview from 'shared/components/community/feed/CommunityPreview'

import CourseCanceled from './courseCanceled'
import Countdown from './Countdown'
import ContentPageBeforeStartDate from './СontentPageBeforeStartDate'

const ContentPageCont = observer(
  ({
    contentPageId,
    contentPagePreviewScope,
    preloadedContentPage,
    pathname,
    urlParams,
    queryParams,
    historyPush,
    LinkComponent,
    contentPageStore,
    userStore,
  }) => (
    <ContentPage
      id={contentPageId}
      scope={contentPagePreviewScope}
      contentPageData={preloadedContentPage?.contentPageData}
      screenSize={preloadedContentPage?.screenSize}
      LinkComponent={LinkComponent}
      historyPush={historyPush}
      urlParams={urlParams}
      queryParams={queryParams}
      pathname={pathname}
      headerHidden
      contentPageStore={contentPageStore}
      userStore={userStore}
    />
  )
)

class Content extends Component {
  @observable editMode = false
  @observable membersPreviewMode = false

  constructor(props) {
    super(props)
    makeObservable(this)
  }

  componentDidMount() {
    const {
      courseViewsStore: { activeLesson },
    } = this.props

    if (activeLesson.name) {
      document.title = activeLesson.name
    } else {
      this.selectLessonFromUrl()
    }
  }

  selectLessonFromUrl = () => {
    const {
      courseViewsStore: { selectLesson },
    } = this.props
    const { lesson_id } = getSearchParams()
    if (lesson_id) {
      selectLesson(lesson_id)
    }
  }

  @action setEditMode = (editMode) => {
    this.editMode = editMode
  }

  @action setMembersPreviewMode = (membersPreviewMode) => {
    this.membersPreviewMode = membersPreviewMode
  }

  render() {
    const {
      activeLesson: { active },
      refreshCountdown,
      activeLesson,
      activeLessonStatus,
      activeCommunity,
      courseSession: { subscribedFrom, subscribedTill, duration } = {},
      viewConfig: {
        contentPagePreviewScope,
        contentToDisplayKey,
        availableDateFormatted,
        lessonIsAvailable,
        layout,
        isPayerCabinet,
      } = {},
    } = this.props.courseViewsStore
    const {
      preloadedContentPage,
      pathname,
      urlParams,
      queryParams,
      historyPush,
      LinkComponent,
      contentPageStore,
      userStore,
    } = this.props
    const { contentPageBeforeId, contentPageId, contentPageAfterId, showRemainingAccessTime } = activeLesson || {}
    const { publishDate, endDate } = activeLessonStatus || {}
    const displayCountDown = !!(!lessonIsAvailable && publishDate)

    const overByTime = () => (
      <div className='blocked-text'>
        <i className='far fa-clock' />
        <span className='quiz-complete'>
          {I18n.t('react.payer.course.over_by_time', {
            subscribed: formatDateTime(subscribedFrom),
            closed: formatDateTime(subscribedTill),
            duration,
          })}
        </span>
      </div>
    )

    const noStatusCont = () => (
      <div className='blocked-text'>
        <i className='far fa-clock' />
        <span className='quiz-complete'>{I18n.t('react.payer.lesson.newLessonAdded')}</span>
      </div>
    )

    const blockedCont = () => (
      <div className='blocked-text'>
        <i className='fas fa-lock' />
        <span className='quiz-complete'>{I18n.t('react.payer.lesson.access_blocked')}</span>
      </div>
    )

    const canceledCont = () => (
      <div className='blocked-text'>
        <CourseCanceled />
        <div className='quiz-complete__title'>{I18n.t('react.payer.course.status.course_canceled.title')}</div>
        <span className='quiz-complete'>{I18n.t('react.payer.course.status.course_canceled.content')}</span>
      </div>
    )

    const accessExpiredCont = () => (
      <div className='blocked-text'>
        <i className='fas fa-lock' />
        <span className='quiz-complete'>{I18n.t('react.payer.lesson.access_expired')}</span>
      </div>
    )

    const completeQuizCont = () => (
      <div className='blocked-text'>
        <i className='fas fa-lock' />
        <span className='quiz-complete'>{I18n.t('react.payer.lesson.quiz.required_to_complete')}</span>
      </div>
    )

    const contentIsNotAvailable = () => (
      <div className='blocked-text'>
        <i className='fas fa-lock' />
        <span className='quiz-complete'>{I18n.t('react.payer.lesson.content_is_not_available')}</span>
      </div>
    )

    const willBeAvailableCont = () => (
      <div className='blocked-text'>
        <i className='far fa-clock' />
        <span className='will-be'>{I18n.t('react.payer.lesson.will_be_available')}</span>
        <Countdown date={new Date(availableDateFormatted)} refreshCountdown={refreshCountdown} />
      </div>
    )

    const contentPageAfterCont = () => (
      <ContentPage
        id={contentPageAfterId}
        scope={contentPagePreviewScope}
        countries={this.props.countries}
        headerHidden
        contentPageData={preloadedContentPage?.contentPageData}
        screenSize={preloadedContentPage?.contentPageData}
        contentPageStore={contentPageStore}
        userStore={userStore}
      />
    )

    const contentPageBeforeCont = () => (
      <ContentPageBeforeStartDate
        {...{
          displayCountDown,
          contentPageBeforeId,
          contentPagePreviewScope,
          refreshCountdown,
          publishDate,
          preloadedContentPage,
          LinkComponent,
          historyPush,
          urlParams,
          queryParams,
          pathname,
          contentPageStore,
          userStore,
        }}
      />
    )

    const ContentToDisplay = {
      overByTime,
      noStatusCont,
      blockedCont,
      canceledCont,
      accessExpiredCont,
      completeQuizCont,
      willBeAvailableCont,
      contentPageAfterCont,
      contentPageBeforeCont,
      contentPageCont: ContentPageCont,
      contentIsNotAvailable,
    }[contentToDisplayKey]

    const isDefaultTheme = layout === THEMES.default

    const contentClasses = classNames(COURSE_CONTENT_CLASS_NAMES.containerClassName, { 'white-widget': isDefaultTheme })

    const { weeks, days, hours, minutes } = getTimeLeftObject(endDate, false)

    const remainingAccessData = [
      {
        unitValue: weeks,
        unitText: weeks > 1 ? I18n.t('react.payer.lesson.weeks') : I18n.t('react.payer.lesson.week'),
      },
      {
        unitValue: days,
        unitText: days > 1 ? I18n.t('react.payer.lesson.days') : I18n.t('react.payer.lesson.day'),
      },
      {
        unitValue: hours,
        unitText: hours > 1 ? I18n.t('react.payer.lesson.hours') : I18n.t('react.payer.lesson.hour'),
      },
      {
        unitValue: minutes,
        unitText: minutes > 1 ? I18n.t('react.payer.lesson.mins') : I18n.t('react.payer.lesson.min'),
      },
    ]

    const getAccessRemainingContent = () =>
      remainingAccessData.map(
        (option, index) =>
          !!option.unitValue && (
            <span key={index} className='lesson-page__banner--comma-separated'>
              {option.unitValue} {option.unitText}
            </span>
          )
      )

    const bannerClasses = classNames('lesson-page__banner', {
      'lesson-page__banner--custom': !isDefaultTheme,
    })
    const isAccessBannerVisible = active && showRemainingAccessTime && contentToDisplayKey === 'contentPageCont'

    return (
      <>
        {isAccessBannerVisible && (
          <div className={bannerClasses}>
            <EloInfoMessage icon={<EloInfoIcon />} status='information'>
              {I18n.t('react.payer.lesson.remaining_access_time')} {getAccessRemainingContent()}
            </EloInfoMessage>
          </div>
        )}
        <div className={contentClasses}>
          {active && isDefaultTheme && <span className='lesson-page__title'>{activeLesson.name}</span>}
          {activeCommunity?.id ? (
            <CommunityPreview
              isPayerCabinet={isPayerCabinet}
              payerCommunityId={activeCommunity?.id}
              editMode={this.editMode}
              setEditMode={this.setEditMode}
              membersPreviewMode={this.membersPreviewMode}
              setMembersPreviewMode={this.setMembersPreviewMode}
              historyPush={historyPush}
              urlCommunityId={urlParams.id}
            />
          ) : (
            <ContentToDisplay
              contentPageId={contentPageId}
              contentPagePreviewScope={contentPagePreviewScope}
              preloadedContentPage={preloadedContentPage}
              pathname={pathname}
              urlParams={urlParams}
              queryParams={queryParams}
              historyPush={historyPush}
              LinkComponent={LinkComponent}
              contentPageStore={contentPageStore}
              userStore={userStore}
            />
          )}
        </div>
      </>
    )
  }
}

Content.displayName = 'Content'

const stores = ['courseViewsStore', 'contentPageStore', 'userStore']

export default withStores(stores, Content)
